import React from 'react';
import { makeStyles } from '@material-ui/core';

import { useIntl } from 'ha/i18n';
import MenuList from 'ha/ui/next/MenuList';
import ListItemIcon from 'ha/ui/next/ListItemIcon';
import ListItemText from 'ha/ui/next/ListItemText';
import { Typography } from 'ha/ui/next/Typography';
import Divider from 'ha/ui/next/Divider';

import ArrowLeftIcon from 'streamline-icons/arrow-button-left-bold.svg';
import CreditCardIcon from 'streamline-icons/credit-card-1-bold.svg';
import PaymentCoinIcon from 'streamline-icons/cash-payment-coin-bold.svg';
import TaskListIcon from 'streamline-icons/task-list-cash-bold.svg';
import TimeCashIcon from 'streamline-icons/time-clock-file-cash-bold.svg';
import DocumentIcon from 'streamline-icons/accounting-document-bold.svg';
import CoinsIcon from 'streamline-icons/accounting-coins-stack-bold.svg';

import MenuItem from './MenuItem';

const useStyles = makeStyles(theme => ({
  title: {
    margin: '0.5rem 1rem',
  },
  tenantsTitle: {
    margin: '1rem 1rem 0',
  },
  divider: {
    padding: '0 1rem',
    margin: '1rem 0',
  },
  landlordsTitle: {
    margin: '0 1rem',
  },
}));

interface Props {
  openMainMenu: () => void;
  isAdvertiser: boolean;
}

export const MobilePaymentMenu: React.FC<Props> = ({
  openMainMenu,
  isAdvertiser,
}) => {
  const { T, urlResolver } = useIntl();
  const classes = useStyles();

  return (
    <MenuList disablePadding>
      <MenuItem onClick={openMainMenu} divider>
        <ListItemIcon iconSize="small">
          <ArrowLeftIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          <Typography.Caption color="muted" textTransform="uppercase">
            {T('navbar_mobile_back')}
          </Typography.Caption>
        </ListItemText>
      </MenuItem>
      <div className={classes.title}>
        <Typography.Paragraph textTransform="uppercase">
          {T('navbar_mobile_payment_title')}
        </Typography.Paragraph>
      </div>
      <MenuList disablePadding>
        {isAdvertiser && (
          <div className={classes.tenantsTitle}>
            <Typography.Caption color="muted">
              {T('navbar_tenants_title')}
            </Typography.Caption>
          </div>
        )}
        <MenuItem
          to={urlResolver.getPaymentMethodsUrl()}
          data-test-locator="Payment methods"
        >
          <ListItemIcon iconSize="small">
            <CreditCardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{T('Payment methods')}</ListItemText>
        </MenuItem>
        <MenuItem
          to={urlResolver.getPaymentsPaymentRequestsUrl()}
          data-test-locator="Tenant payment requests"
        >
          <ListItemIcon iconSize="small">
            <PaymentCoinIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{T('Payment requests')}</ListItemText>
        </MenuItem>
      </MenuList>
      {isAdvertiser && (
        <div className={classes.divider}>
          <Divider />
        </div>
      )}
      {isAdvertiser && (
        <MenuList disablePadding>
          <div className={classes.landlordsTitle}>
            <Typography.Caption color="muted">
              {T('navbar_landlords_title')}
            </Typography.Caption>
          </div>
          <MenuItem
            to={urlResolver.getPayoutMethodsUrl()}
            data-test-locator="Payout preferences"
          >
            <ListItemIcon iconSize="small">
              <CreditCardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{T('Payout preferences')}</ListItemText>
          </MenuItem>
          <MenuItem
            to={urlResolver.getBillingUrl()}
            data-test-locator="Billing details"
          >
            <ListItemIcon iconSize="small">
              <TaskListIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{T('Billing details')}</ListItemText>
          </MenuItem>
          <MenuItem
            to={urlResolver.getTransactionHistoryUrl()}
            data-test-locator="Transaction history"
          >
            <ListItemIcon iconSize="small">
              <TimeCashIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{T('Transaction history')}</ListItemText>
          </MenuItem>
          <MenuItem
            to={urlResolver.getPayoutsPaymentRequests()}
            data-test-locator="Landlord payment requests"
          >
            <ListItemIcon iconSize="small">
              <PaymentCoinIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{T('Payment requests')}</ListItemText>
          </MenuItem>
          <MenuItem
            to={urlResolver.getInvoicesUrl()}
            data-test-locator="Invoices"
          >
            <ListItemIcon iconSize="small">
              <DocumentIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{T('Invoices')}</ListItemText>
          </MenuItem>
          <MenuItem
            to={urlResolver.getCommissionPlanUrl()}
            data-test-locator="Commission plan"
          >
            <ListItemIcon iconSize="small">
              <CoinsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{T('Commission plan')}</ListItemText>
          </MenuItem>
        </MenuList>
      )}
    </MenuList>
  );
};

export default MobilePaymentMenu;
